import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import {
  discardMappingDraft,
  fetchMappingVersions,
  publishMappingDraft,
  RequestError,
  rollbackMappingVersion,
  saveMappingDraft,
  type MappingVersionsState,
} from '../../api/dataApi';
import { AppCard } from '../ui/AppCard';
import { Button } from '../ui/Button';
import { MappingVisualPreview } from '../../screens/integrations/MappingVisualPreview';
import { formatRunTime } from '../../lib/integrationHealth';
import type { Mapping } from '../../types';

const sourceLabel: Record<string, string> = {
  initial: 'Baseline',
  publish: 'Published',
  rollback: 'Rollback',
  edit: 'Edited directly',
};

const pretty = (v: unknown) => JSON.stringify(v ?? {}, null, 2);

// Draft / publish / rollback for one Integration's Mapping (roadmap #9 Phase
// 3). The owner edits a DRAFT of the active spec - which changes nothing
// live - previews it against a real sample, then publishes it as the next
// version, atomically; a bad version is undone with one Roll back (which
// publishes a copy of the old rules as a new version, so history stays
// linear). Both only affect FUTURE deliveries; nothing is replayed.
// Credentials are never shown here, never saved into a draft and never
// restored by a rollback (backend/internal/mappingversion).
export function VersionsSection({ mapping, onChanged, reloadKey }: { mapping: Mapping; onChanged?: () => void; reloadKey?: number }) {
  const nickname = mapping.nickname;
  const [state, setState] = useState<MappingVersionsState | null>(null);
  const [text, setText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<null | 'save' | 'publish' | 'discard' | 'rollback'>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await fetchMappingVersions(nickname);
      const t = pretty(s.draft ? s.draft.spec : s.activeSpec);
      setState(s);
      setText(t);
      setSavedText(s.draft ? t : '');
      setConflict(false);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load versions.');
    }
  }, [nickname]);

  // reloadKey: the visual editor saved a draft, so re-read it into the JSON box.
  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const parsed = useMemo<{ spec: Record<string, unknown> | null; problem: string | null }>(() => {
    try {
      const v = JSON.parse(text);
      if (v === null || typeof v !== 'object' || Array.isArray(v)) return { spec: null, problem: 'The spec must be a JSON object.' };
      return { spec: v as Record<string, unknown>, problem: null };
    } catch (e) {
      return { spec: null, problem: e instanceof Error ? e.message : 'Invalid JSON.' };
    }
  }, [text]);

  if (!state) {
    return error ? (
      <p className="text-sm text-red-500">{error}</p>
    ) : (
      <div className="py-3 text-center text-sm text-black/40 dark:text-white/40">
        <Loader2 size={14} className="inline animate-spin" /> Loading versions…
      </div>
    );
  }

  const hasDraft = !!state.draft;
  const activeText = pretty(state.activeSpec);
  const differsFromActive = text !== activeText;
  const dirty = hasDraft ? text !== savedText : differsFromActive;
  const canPublish = hasDraft && !dirty && !parsed.problem;

  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof RequestError && e.status === 409) {
        setConflict(true);
      } else {
        setError(e instanceof Error ? e.message : 'Something went wrong.');
      }
    } finally {
      setBusy(null);
    }
  };

  const saveDraft = () =>
    run('save', async () => {
      if (!parsed.spec) return;
      await saveMappingDraft(nickname, parsed.spec, state.activeVersion);
      await load();
    });

  const discard = () =>
    run('discard', async () => {
      await discardMappingDraft(nickname);
      await load();
    });

  const publish = () =>
    run('publish', async () => {
      await publishMappingDraft(nickname, state.activeVersion, note.trim());
      setNote('');
      setShowPreview(false);
      await load();
      onChanged?.();
    });

  const rollback = (to: number) =>
    run('rollback', async () => {
      await rollbackMappingVersion(nickname, to, state.activeVersion);
      setConfirmRollback(null);
      await load();
      onChanged?.();
    });

  const active = state.versions.find((v) => v.version === state.activeVersion);

  return (
    <div className="flex flex-col gap-3">
      <AppCard className="flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">Active version v{state.activeVersion}</span>
          {active && (
            <span className="text-xs text-black/45 dark:text-white/45">
              {sourceLabel[active.source] ?? active.source} · {formatRunTime(active.publishedAt)}
            </span>
          )}
        </div>
        <span className="text-xs text-black/50 dark:text-white/50">
          Edit a draft below - it changes nothing live until you publish it. Publishing or rolling back only affects
          deliveries that arrive afterwards; nothing is replayed.
        </span>
      </AppCard>

      {conflict && (
        <div role="alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm flex items-center gap-2 flex-wrap">
          <span>This integration changed since you loaded it (someone published a newer version).</span>
          <Button variant="bordered" onClick={() => void load()}>
            Reload
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-black/60 dark:text-white/60">{hasDraft ? `Draft (from v${state.draft!.baseVersion})` : 'Draft'}</span>
          {dirty && <span className="text-amber-600 dark:text-amber-400">unsaved changes</span>}
          {hasDraft && !dirty && <span className="text-emerald-600 dark:text-emerald-400">saved</span>}
        </div>
        <textarea
          aria-label="Draft spec (JSON)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          rows={12}
          className="w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 p-3 text-[13px] font-mono leading-relaxed outline-none focus:border-blue-500"
        />
        {parsed.problem && <span className="text-xs text-red-500">{parsed.problem}</span>}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => void saveDraft()} disabled={!!busy || !!parsed.problem || (!dirty && hasDraft) || (!hasDraft && !differsFromActive)}>
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </Button>
          <Button variant="bordered" onClick={() => setShowPreview((p) => !p)} disabled={!parsed.spec}>
            {showPreview ? 'Hide preview' : 'Test with a sample'}
          </Button>
          {hasDraft && (
            <Button variant="bordered" onClick={() => void discard()} disabled={!!busy}>
              {busy === 'discard' ? 'Discarding…' : 'Discard draft'}
            </Button>
          )}
          {differsFromActive && !hasDraft && (
            <Button variant="bordered" onClick={() => setText(activeText)}>
              Reset to active
            </Button>
          )}
        </div>
      </div>

      {showPreview && parsed.spec && <MappingVisualPreview mapping={{ ...mapping, spec: parsed.spec }} />}

      {hasDraft && (
        <AppCard className="flex flex-col gap-2">
          <span className="text-sm font-semibold">Publish v{state.activeVersion + 1}</span>
          <input
            aria-label="What changed (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="What changed? (optional)"
            className="rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2 text-sm outline-none focus:border-blue-500"
          />
          <div className="flex items-center gap-2">
            <Button onClick={() => void publish()} disabled={!canPublish || !!busy}>
              {busy === 'publish' ? 'Publishing…' : `Publish as v${state.activeVersion + 1}`}
            </Button>
            {!canPublish && dirty && <span className="text-xs text-black/45 dark:text-white/45">Save the draft first.</span>}
          </div>
        </AppCard>
      )}

      <div className="flex flex-col gap-1">
        <span className="text-[11px] text-black/40 dark:text-white/40">History</span>
        <div className="flex flex-col divide-y divide-black/5 dark:divide-white/5">
          {state.versions.map((v) => {
            const isActive = v.version === state.activeVersion;
            return (
              <div key={v.version} className="flex items-start gap-2 py-2 text-xs">
                <span className="w-9 shrink-0 font-mono font-semibold">v{v.version}</span>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-black/70 dark:text-white/70">
                    {sourceLabel[v.source] ?? v.source}
                    {v.rolledBackTo ? ` (copy of v${v.rolledBackTo})` : ''}
                    {isActive && <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">active</span>}
                  </span>
                  {v.note && <span className="text-black/45 dark:text-white/45">{v.note}</span>}
                  <span className="text-black/35 dark:text-white/35">
                    {formatRunTime(v.publishedAt)} · {v.publishedBy}
                  </span>
                </div>
                {!isActive &&
                  (confirmRollback === v.version ? (
                    <span className="flex items-center gap-1 shrink-0">
                      <Button variant="bordered" onClick={() => void rollback(v.version)} disabled={!!busy}>
                        {busy === 'rollback' ? 'Rolling back…' : `Publish as v${state.activeVersion + 1}`}
                      </Button>
                      <Button variant="bordered" onClick={() => setConfirmRollback(null)}>
                        Cancel
                      </Button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmRollback(v.version)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/20 px-2 py-1 text-black/55 dark:text-white/55 hover:text-blue-600 hover:border-blue-400"
                    >
                      <RotateCcw size={12} /> Roll back to this
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
      {error && <p className="text-xs text-red-500 m-0">{error}</p>}
    </div>
  );
}
